import { ArrowLeft, Bell, ChevronDown, LogOut, Search } from 'lucide-react';
import { EmptyState, cn } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { LocalePicker } from '../../shared/i18n/LocalePicker.js';
import { ThemeToggle } from '../../shared/theme/ThemeToggle.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../shared/ui/useMediaQuery.js';
import { Menu } from './Menu.js';
import { SettingsMenu } from './SettingsMenu.js';
import { Kbd } from './CommandPalette.js';

/** macOS da `⌘`, qolgan joyda `Ctrl` — yorliq yozuvi haqiqatga mos bo'lsin. */
export const isAppleKeyboard = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent);

export interface TopbarProps {
  /** Chapdagi nom — joriy bo'lim. */
  sectionLabel: string;
  onOpenPalette: () => void;
  /** Xodimning ismi; sessiya hali tiklanmagan bo'lsa `null`. */
  userName: string | null;
  userRole: string;
  onLogout: () => void;
  /**
   * O'qilmagan bildirishnomalar soni. Manba hali ulanmagan — shuning uchun
   * `0`: qizil nuqta faqat haqiqatan xabar bo'lganda yonadi.
   */
  unreadCount?: number;
  /**
   * Ichki sahifadan qaytish. Berilmasa strelka umuman chizilmaydi —
   * bo'lim boshida "orqaga" ning borar joyi yo'q.
   *
   * Qaror karkasda qabul qilinadi (`AppShell`): manzil bo'limning
   * o'zimi yoki uning ichidami — shu yerda emas, chunki bu qator
   * navigatsiya tuzilmasini bilmaydi.
   */
  onBack?: () => void;
}

/**
 * Yuqori qator — IKKI XIL yig'iladi.
 *
 * Monitorda: breadcrumb, keng qidiruv maydoni `Ctrl K` yorlig'i bilan,
 * bildirishnoma, til, tema va hisob. U yerda joy yetarli va bularning
 * har biri bir bosishni tejaydi.
 *
 * Telefon va planshetda esa qator qisqaradi — orqaga/nom, lupa, sozlamalar
 * va hisob. Qolgani `display:none` bilan yashirilmaydi, CHIZILMAYDI:
 * yashirilgan breadcrumb ekran o'qigich uchun baribir o'qiladi va sahifa
 * nomi ikki marta aytilardi. Til bilan tema esa alohida tugmalar bo'lib
 * emas, `SettingsMenu` ostida turadi — ular bir marta tanlanadigan sozlama
 * bo'la turib, qatorda ikkita joyni yeb turardi.
 *
 * `sticky` — uzun jadvalni aylantirganda ham navigatsiya qo'l ostida qoladi.
 */
export const Topbar = ({
  sectionLabel,
  onOpenPalette,
  userName,
  userRole,
  onLogout,
  unreadCount = 0,
  onBack,
}: TopbarProps) => {
  const hasUnread = unreadCount > 0;
  const desktop = useMediaQuery(DESKTOP_QUERY);

  return (
    <header
      className={cn(
        // `chrome` — eng baland yopishqoq qatlam. Sahifa ichidagi
        // hech qanday panel headerni to'sa olmaydi: ilgari ikkalasi
        // ham `--z-sticky` da turardi va ustunlikni DOM tartibi hal
        // qilardi, ya'ni sahifadagi panel doim yutardi.
        'sticky top-0 z-[var(--z-chrome)] flex shrink-0 items-center',
        'gap-3 border-b border-border bg-surface/80 px-3 backdrop-blur-md sm:px-4',
        // Balandlik TOKENDAN: telefonda 56px, keng ekranda 64px, ikkalasiga
        // ham `notch` ostidagi xavfsiz zona qo'shiladi. Ostiga yopishadigan
        // panellar ayni shu o'zgaruvchini o'qiydi — ular hech qachon
        // ajralib qolmaydi.
        //
        // Ilgari bu yerda `h-14` turardi va `padding` `border-box` ichiga
        // tushib, xavfsiz zona balandlikka QO'SHILMASDI: `notch` li
        // telefonda qator 56px dan 9px ga siqilib qolardi.
        'h-[var(--size-topbar)] pt-[env(safe-area-inset-top)]',
      )}
    >
      {desktop ? (
        <nav aria-label={t('topbar.breadcrumb')} className="flex min-w-0 items-center gap-2">
          <span className="text-sm text-text-faint">{t('app.name')}</span>
          <span aria-hidden className="text-text-faint">
            /
          </span>
          <span
            aria-current="page"
            className="truncate text-sm font-semibold tracking-tight text-text"
          >
            {sectionLabel}
          </span>
        </nav>
      ) : (
        <>
          {onBack === undefined ? null : (
            <button
              type="button"
              onClick={onBack}
              aria-label={t('topbar.back')}
              className={cn(
                'flex size-11 shrink-0 items-center justify-center',
                'rounded-[var(--radius-sm)] text-text-muted',
                'transition-[background-color,color,transform] duration-[var(--motion-fast)]',
                'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text',
                'active:scale-[0.96]',
              )}
            >
              <ArrowLeft size={20} aria-hidden />
            </button>
          )}
          {/* Sahifa nomi — bitta qatorda. Bu YAGONA sarlavha emas: kontent
              tepasida ham turadi, shuning uchun uzun nom bu yerda
              qisqartirilsa ham ma'lumot yo'qolmaydi. */}
          <span className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-text">
            {sectionLabel}
          </span>
        </>
      )}

      {/* Qidiruv o'rtada va kengayadi — u eng ko'p ishlatiladigan yo'l. */}
      {desktop ? (
        <button
          type="button"
          onClick={onOpenPalette}
          className={cn(
            'mx-auto flex h-[var(--size-control-sm)] w-full max-w-[22rem] min-w-0 items-center gap-2',
            'rounded-[var(--radius-sm)] border border-border bg-surface-2 px-2.5',
            'text-sm text-text-faint',
            'transition-[border-color,color] duration-[var(--motion-fast)]',
            'ease-[var(--ease-standard)] hover:border-border-strong hover:text-text-muted',
          )}
        >
          <Search size={15} aria-hidden className="shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">{t('topbar.search')}</span>
          {/* Klaviatura yorlig'i FAQAT klaviaturasi bor qurilmada:
              teginish ekranida u bajarib bo'lmaydigan ko'rsatma. */}
          <span className="ml-0 hidden shrink-0 items-center gap-1 [@media_(hover:hover)_and_(pointer:fine)]:flex">
            <Kbd>{isAppleKeyboard() ? '⌘' : 'Ctrl'}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label={t('topbar.searchOpen')}
          className={cn(
            'flex size-11 shrink-0 items-center justify-center',
            'rounded-[var(--radius-sm)] text-text-muted',
            'transition-[background-color,color,transform] duration-[var(--motion-fast)]',
            'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text',
            'active:scale-[0.96]',
          )}
        >
          <Search size={20} aria-hidden />
        </button>
      )}

      <div className="flex shrink-0 items-center gap-1">
        {/* Bildirishnoma, til va tema — FAQAT monitorda: u yerda joy
            yetarli va har biri bir bosishni tejaydi. Telefonda til bilan
            tema pastdagi `SettingsMenu` ostiga yig'iladi, qo'ng'iroq esa
            hozircha bo'sh ro'yxatdan boshqa narsa ko'rsatmaydi va tor
            qatordan joy olishga arzimaydi. */}
        {desktop ? (
          <>
            <Menu
              label={
                hasUnread
                  ? t('topbar.notificationsUnread', { count: unreadCount })
                  : t('topbar.notifications')
              }
              triggerClassName="relative size-9 justify-center"
              panelClassName="w-[19rem]"
              trigger={
                <>
                  <Bell size={17} aria-hidden />
                  {hasUnread ? (
                    <span
                      aria-hidden
                      className={cn(
                        'absolute top-1.5 right-1.5 size-2 rounded-[var(--radius-pill)]',
                        'bg-danger ring-2 ring-surface',
                      )}
                    />
                  ) : null}
                </>
              }
            >
              <EmptyState
                className="px-4 py-8"
                icon={<Bell size={18} aria-hidden />}
                title={t('topbar.notificationsEmpty')}
                description={t('topbar.notificationsEmptyHint')}
              />
            </Menu>

            {/* TIL — kirish sahifasida ham, ish ichida ham. Ilgari u faqat
                kirishda turardi va tilni almashtirish uchun tizimdan chiqish
                kerak bo'lardi. */}
            <LocalePicker />

            <ThemeToggle withLabel={false} />
          </>
        ) : null}

        {/* SOZLAMALAR — til va ko'rinish. Monitorda ular yuqorida alohida
            tugma bo'lib turadi (yuqoridagi shox), shuning uchun bu yerda
            menyu FAQAT tor ekranda kerak. */}
        {desktop ? null : <SettingsMenu />}

        <Menu
          label={t('topbar.account')}
          // Telefonda 44px nishon — barmoq uchun eng kichik o'lchov.
          triggerClassName="size-11 justify-center lg:h-9 lg:w-auto lg:px-2"
          panelClassName="w-[15rem]"
          trigger={
            <>
              <span
                aria-hidden
                className={cn(
                  'flex size-7 shrink-0 items-center justify-center lg:size-6',
                  'rounded-[var(--radius-pill)] bg-surface-2 text-xs font-semibold text-text-muted',
                )}
              >
                {(userName ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="hidden max-w-[9rem] truncate text-sm font-medium text-text lg:block">
                {userName ?? '—'}
              </span>
              <ChevronDown size={14} aria-hidden className="hidden shrink-0 lg:block" />
            </>
          }
        >
          <div className="border-b border-border px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-text">{userName ?? '—'}</p>
            <p className="truncate text-xs text-text-faint">{userRole}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className={cn(
              'flex h-10 w-full items-center gap-2 px-3 text-left text-sm text-text-muted',
              'transition-[background-color,color] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-danger',
            )}
          >
            <LogOut size={15} aria-hidden />
            {t('topbar.logout')}
          </button>
        </Menu>
      </div>
    </header>
  );
};
